import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Users, 
  MessageSquare, 
  TrendingUp, 
  Send,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Loader2,
  Database,
  Bell,
  UserPlus,
  Clock,
  Wifi,
  WifiOff,
  Mail,
  Sparkles,
  Bot,
  ArrowRightLeft,
  Timer
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Random names for simulation
const FAKE_NAMES = [
  'Roberto Méndez', 'Patricia Vega', 'Fernando Ríos', 'Claudia Serrano',
  'Miguel Ángel Torres', 'Sofía Ramírez', 'Alejandro Cruz', 'Valeria Moreno',
  'Luis Fernando Díaz', 'Carolina Jiménez', 'Ricardo Gómez', 'Daniela Vargas'
];

const FAKE_MESSAGES = [
  'Hola, necesito cotizar transporte para 15 personas',
  'Buenos días, ¿tienen disponibilidad para mañana?',
  'Quiero información sobre sus servicios de tour',
  'Hola, busco transporte ejecutivo al aeropuerto',
  '¿Cuánto cuesta el servicio de camioneta para boda?',
  'Necesito transporte para evento corporativo'
];

const Dashboard = () => {
  const { token } = useAuth();
  const { notifications, notifyNewLead, playSound, socketConnected } = useNotifications();
  const [analytics, setAnalytics] = useState(null);
  const [botStats, setBotStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [recentLeads, setRecentLeads] = useState([]);
  const [showNewLeadAlert, setShowNewLeadAlert] = useState(false);
  const [businessHoursStatus, setBusinessHoursStatus] = useState(null);
  const previousContactsRef = useRef(0);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBotStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/bot-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBotStats(data);
      }
    } catch (error) {
      console.error('Error fetching bot stats:', error);
    }
  };

  const fetchBusinessHoursStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/business-hours/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBusinessHoursStatus(data);
      }
    } catch (error) {
      console.error('Error fetching business hours:', error);
    }
  };

  const seedDemoData = async () => {
    setSeeding(true);
    try {
      const response = await fetch(`${API_URL}/api/seed-demo-data`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        // Trigger new lead alert
        setShowNewLeadAlert(true);
        playSound();
        notifyNewLead('Nuevos contactos de demo', 'Varios');
        
        setTimeout(() => setShowNewLeadAlert(false), 5000);
        await fetchAnalytics();
        await fetchRecentLeads();
      }
    } catch (error) {
      console.error('Error seeding data:', error);
    } finally {
      setSeeding(false);
    }
  };

  const simulateNewLead = async () => {
    setSimulating(true);
    try {
      // Generate random lead data
      const randomName = FAKE_NAMES[Math.floor(Math.random() * FAKE_NAMES.length)];
      const randomMessage = FAKE_MESSAGES[Math.floor(Math.random() * FAKE_MESSAGES.length)];
      const randomPhone = `+5233${Math.floor(10000000 + Math.random() * 90000000)}`;

      const response = await fetch(`${API_URL}/api/simulate-whatsapp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          phone: randomPhone,
          message: randomMessage,
          name: randomName
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // Show alert
        setShowNewLeadAlert(true);
        playSound();
        
        // Show toast with assignment info
        if (result.assigned_to) {
          toast.success(`¡Nuevo lead simulado!`, {
            description: `${randomName} asignado a ${result.assigned_to}`,
            duration: 5000
          });
        } else if (result.bot_active) {
          toast.success(`¡Nuevo lead simulado!`, {
            description: `${randomName} - Bot activo, mensaje de bienvenida enviado`,
            duration: 5000
          });
        }
        
        setTimeout(() => setShowNewLeadAlert(false), 5000);
        
        // Refresh data
        await fetchAnalytics();
        await fetchRecentLeads();
      } else {
        toast.error('Error al simular lead');
      }
    } catch (error) {
      console.error('Error simulating lead:', error);
      toast.error('Error al simular lead');
    } finally {
      setSimulating(false);
    }
  };

  const fetchRecentLeads = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts?stage=nuevo`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRecentLeads(data.slice(0, 5));
        
        // Check for new leads
        if (previousContactsRef.current > 0 && data.length > previousContactsRef.current) {
          const newCount = data.length - previousContactsRef.current;
          setShowNewLeadAlert(true);
          playSound();
          setTimeout(() => setShowNewLeadAlert(false), 5000);
        }
        previousContactsRef.current = data.length;
      }
    } catch (error) {
      console.error('Error fetching recent leads:', error);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    fetchBotStats();
    fetchRecentLeads();
    fetchBusinessHoursStatus();
    
    // Update business hours status every minute
    const interval = setInterval(() => {
      fetchBusinessHoursStatus();
      fetchBotStats();
    }, 60000);
    
    return () => clearInterval(interval);
  }, [token]);

  const STAGE_COLORS = {
    nuevo: '#3B82F6',
    contactado: '#8B5CF6',
    interesado: '#F59E0B',
    propuesta: '#10B981',
    cerrado: '#22C55E',
    perdido: '#EF4444'
  };

  const STAGE_NAMES = {
    nuevo: 'Nuevo',
    contactado: 'Contactado',
    interesado: 'Interesado',
    propuesta: 'Propuesta',
    cerrado: 'Cerrado',
    perdido: 'Perdido'
  };

  const pieData = analytics?.leads_by_stage 
    ? Object.entries(analytics.leads_by_stage).map(([stage, count]) => ({
        name: STAGE_NAMES[stage] || stage,
        value: count,
        color: STAGE_COLORS[stage] || '#94a3b8'
      }))
    : [];

  const agentData = analytics?.agent_stats?.map(agent => ({
    name: agent.name?.split(' ')[0] || 'Agente',
    mensajes: agent.messages_sent,
    contactos: agent.contacts_assigned
  })) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 text-[#25D366] animate-spin" />
      </div>
    );
  }

  const isEmpty = !analytics || (analytics.total_contacts === 0 && analytics.total_messages === 0);

  // Filter lead notifications
  const leadNotifications = notifications.filter(n => n.type === 'new_lead').slice(0, 3);

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8">
      {/* Status Bar */}
      <div className="mb-6 flex items-center gap-4 flex-wrap">
        {/* Socket Connection Status */}
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
          socketConnected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {socketConnected ? (
            <>
              <Wifi className="w-4 h-4" />
              <span>Tiempo real activo</span>
            </>
          ) : (
            <>
              <WifiOff className="w-4 h-4" />
              <span>Conectando...</span>
            </>
          )}
        </div>

        {/* Business Hours Status */}
        {businessHoursStatus && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
            businessHoursStatus.is_business_hours 
              ? 'bg-green-100 text-green-700' 
              : 'bg-orange-100 text-orange-700'
          }`}>
            <Clock className="w-4 h-4" />
            <span>
              {businessHoursStatus.is_business_hours 
                ? `Horario laboral - ${businessHoursStatus.day_of_week}` 
                : 'Fuera de horario'}
            </span>
          </div>
        )}

        {/* Bot Status */}
        {businessHoursStatus && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
            businessHoursStatus.bot_active 
              ? 'bg-purple-100 text-purple-700' 
              : 'bg-slate-100 text-slate-600'
          }`}>
            <span className="text-lg">🤖</span>
            <span>
              {businessHoursStatus.bot_active 
                ? 'Bot activo (fuera de horario)' 
                : 'Bot inactivo (horario laboral)'}
            </span>
          </div>
        )}

        {/* Email Alert Status */}
        {businessHoursStatus && !businessHoursStatus.is_business_hours && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm bg-blue-100 text-blue-700">
            <Mail className="w-4 h-4" />
            <span>Alertas email ({businessHoursStatus.email_service})</span>
          </div>
        )}
      </div>

      {/* New Lead Alert Banner */}
      {showNewLeadAlert && (
        <div className="mb-6 animate-pulse">
          <div className="bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white p-4 rounded-lg shadow-lg flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center animate-bounce">
              <UserPlus className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg">¡Nuevo lead en el pipeline!</h3>
              <p className="text-white/90">Se ha agregado un nuevo contacto. Revisa tu pipeline para dar seguimiento.</p>
            </div>
            <Button 
              variant="secondary" 
              onClick={() => setShowNewLeadAlert(false)}
              className="bg-white/20 hover:bg-white/30 text-white border-0"
            >
              Entendido
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Resumen de tu actividad de ventas</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Simulate New Lead Button */}
          <Button
            onClick={simulateNewLead}
            disabled={simulating}
            data-testid="simulate-lead-btn"
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            {simulating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Simular lead nuevo
          </Button>
          
          {isEmpty && (
            <Button
              onClick={seedDemoData}
              disabled={seeding}
              data-testid="seed-demo-data-btn"
              className="bg-[#25D366] hover:bg-[#1DA851] text-white"
            >
              {seeding ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Database className="w-4 h-4 mr-2" />
              )}
              Cargar datos demo
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => { fetchAnalytics(); fetchRecentLeads(); }}
            data-testid="refresh-analytics-btn"
            className="border-slate-200"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="border-slate-100 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Total Contactos</p>
                <p className="text-3xl font-bold font-['Outfit'] text-slate-900">
                  {analytics?.total_contacts || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-500" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-sm">
              <ArrowUpRight className="w-4 h-4 text-green-500" />
              <span className="text-green-600 font-medium">+12%</span>
              <span className="text-slate-500">vs mes anterior</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Conversaciones</p>
                <p className="text-3xl font-bold font-['Outfit'] text-slate-900">
                  {analytics?.total_conversations || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-purple-500" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-sm">
              <ArrowUpRight className="w-4 h-4 text-green-500" />
              <span className="text-green-600 font-medium">+8%</span>
              <span className="text-slate-500">vs mes anterior</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Mensajes Enviados</p>
                <p className="text-3xl font-bold font-['Outfit'] text-slate-900">
                  {analytics?.messages_sent || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                <Send className="w-6 h-6 text-green-500" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-sm">
              <ArrowUpRight className="w-4 h-4 text-green-500" />
              <span className="text-green-600 font-medium">+24%</span>
              <span className="text-slate-500">vs mes anterior</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Mensajes Recibidos</p>
                <p className="text-3xl font-bold font-['Outfit'] text-slate-900">
                  {analytics?.messages_received || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-orange-500" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-sm">
              <ArrowDownRight className="w-4 h-4 text-red-500" />
              <span className="text-red-600 font-medium">-3%</span>
              <span className="text-slate-500">vs mes anterior</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Leads by Stage */}
        <Card className="lg:col-span-5 border-slate-100 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold font-['Outfit']">Leads por Etapa</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-slate-400">
                Sin datos disponibles
              </div>
            )}
            <div className="flex flex-wrap gap-3 mt-4 justify-center">
              {pieData.map((entry) => (
                <div key={entry.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                  <span className="text-sm text-slate-600">{entry.name}: {entry.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Agent Performance */}
        <Card className="lg:col-span-7 border-slate-100 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold font-['Outfit']">Rendimiento por Agente</CardTitle>
          </CardHeader>
          <CardContent>
            {agentData.length > 0 ? (
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={agentData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Bar dataKey="mensajes" fill="#25D366" radius={[4, 4, 0, 0]} name="Mensajes" />
                    <Bar dataKey="contactos" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Contactos" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[320px] flex items-center justify-center text-slate-400">
                Sin datos de agentes
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bot Statistics Section */}
      {botStats && (
        <div className="mt-8">
          <Card className="border-slate-100 shadow-sm bg-gradient-to-br from-purple-50 to-white">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold font-['Outfit'] flex items-center gap-2">
                <Bot className="w-5 h-5 text-purple-600" />
                Estadísticas del Bot
                <Badge className={botStats.bot_currently_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                  {botStats.bot_status}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {/* Bot Messages Sent */}
                <div className="bg-white p-4 rounded-lg border border-purple-100">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4 text-purple-500" />
                    <span className="text-xs text-slate-500">Mensajes Bot</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{botStats.bot_messages_sent}</p>
                </div>

                {/* Handoffs to Agent */}
                <div className="bg-white p-4 rounded-lg border border-purple-100">
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowRightLeft className="w-4 h-4 text-blue-500" />
                    <span className="text-xs text-slate-500">Handoffs</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{botStats.handoffs_to_agent}</p>
                </div>

                {/* Conversion Rate */}
                <div className="bg-white p-4 rounded-lg border border-purple-100">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-green-500" />
                    <span className="text-xs text-slate-500">Conversión</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{botStats.conversion_rate}%</p>
                </div>

                {/* Followups Sent */}
                <div className="bg-white p-4 rounded-lg border border-purple-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Timer className="w-4 h-4 text-orange-500" />
                    <span className="text-xs text-slate-500">Followups</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{botStats.followups_sent}</p>
                </div>

                {/* Scheduled Followups */}
                <div className="bg-white p-4 rounded-lg border border-purple-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-yellow-500" />
                    <span className="text-xs text-slate-500">Programados</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{botStats.followups_scheduled}</p>
                </div>

                {/* Recent Activity (7 days) */}
                <div className="bg-white p-4 rounded-lg border border-purple-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-pink-500" />
                    <span className="text-xs text-slate-500">Últimos 7 días</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{botStats.recent_bot_messages_7d}</p>
                </div>
              </div>

              <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                <p className="text-sm text-purple-700">
                  <span className="font-medium">Hora actual en México:</span> {botStats.current_time_mexico} | 
                  <span className="font-medium ml-2">Bot activo fuera de:</span> Lun-Vie 9am-7pm, Sáb 9am-2pm
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Activity Section */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Leads */}
        <Card className="border-slate-100 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold font-['Outfit'] flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#25D366]" />
              Leads Recientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentLeads.length > 0 ? (
              <div className="space-y-3">
                {recentLeads.map((lead) => (
                  <div 
                    key={lead.contact_id} 
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center text-white font-medium">
                      {lead.name?.charAt(0) || 'L'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{lead.name}</p>
                      <p className="text-sm text-slate-500 truncate">{lead.phone}</p>
                    </div>
                    <Badge className="bg-blue-100 text-blue-800">Nuevo</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <UserPlus className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Sin leads nuevos</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Notifications */}
        <Card className="border-slate-100 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold font-['Outfit'] flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#25D366]" />
              Actividad Reciente
            </CardTitle>
          </CardHeader>
          <CardContent>
            {leadNotifications.length > 0 ? (
              <div className="space-y-3">
                {leadNotifications.map((notif) => (
                  <div 
                    key={notif.id} 
                    className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                      notif.read ? 'bg-slate-50' : 'bg-green-50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      notif.read ? 'bg-slate-200' : 'bg-[#25D366]'
                    }`}>
                      <UserPlus className={`w-5 h-5 ${notif.read ? 'text-slate-500' : 'text-white'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{notif.title}</p>
                      <p className="text-sm text-slate-500 truncate">{notif.message}</p>
                    </div>
                    {!notif.read && (
                      <span className="w-2 h-2 bg-[#25D366] rounded-full animate-pulse" />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Bell className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Sin actividad reciente</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
